import { call, fork, put, race, takeLatest, select } from 'redux-saga/effects';
import cookie from 'react-cookies';
import {
  websocketFetch,
  CONFIGURE_CONNECTION,
  AUTHENTICATE_CONNECTION,
  request,
} from 'common/websocket-redux';
import { selectIsInWidgetRunner } from 'common/state/application/application.selectors';
import { setURLToRedirectAfterLogin, getSignInUrl } from 'apps/auth/auth.utils';
import { REDIRECT_TO_SIGN_IN, SIGN_OUT } from '../auth.actions';
import { loadAuthCookies } from './load-auth-cookies';
import { cancelIdleTimer } from 'common/privacy-lock/idle-timer';
import {
  setToken,
  failRefresh,
  JWT_BRIDGE_REFRESH_REQUESTED,
} from 'common/jwt-bridge/jwt-bridge';
import { captureError } from '@leagueplatform/observability';
import { clearSalesforceMessagingSession } from '@leagueplatform/salesforce-live-chat';

const CLEAR_SALESFORCE_SESSION_TIMEOUT_MS = 2500;

const OLD_IDLE_TIME_COOKIE_KEY = 'idle-time-react';

export function* redirectToSignIn(action) {
  const { timeout } = action?.meta?.payload ?? {};
  const isInWidgetRunnerFrame = yield select(selectIsInWidgetRunner);
  const signInUrl = yield call(getSignInUrl, { timeout });

  yield call([cookie, cookie.remove], OLD_IDLE_TIME_COOKIE_KEY);
  if (!isInWidgetRunnerFrame) {
    yield call(setURLToRedirectAfterLogin);
  }
  // uses window.top to ensure WidgetRunner is sent to sign-in
  yield call([window.top.location, window.top.location.assign], signInUrl);
}

export function* authenticate(sessionId, deviceToken) {
  yield request(AUTHENTICATE_CONNECTION, [
    websocketFetch,
    'authenticate',
    {
      session_id: sessionId,
      platform: 'web-tng',
      device_token: deviceToken,
    },
  ]);
}

export function* authenticateWithExistingSession() {
  const { sessionId, deviceToken } = loadAuthCookies();
  if (sessionId && deviceToken) {
    yield call(authenticate, sessionId, deviceToken);
  } else {
    yield put({ type: AUTHENTICATE_CONNECTION.ERRORED });
  }
}

/**
 * JWT Bridge: Call refresh_jwt to obtain a first-party League JWT
 * from the existing cookie-based session (SID2/DT2).
 * Returns the JWT string on success.
 */
export function* refreshJwtToken(sessionId, deviceToken) {
  if (!sessionId || !deviceToken) {
    throw new Error('Missing session_id or device_token for refresh_jwt call');
  }

  const refreshResponse = yield call(websocketFetch, 'refresh_jwt', {
    session_id: sessionId,
    device_token: deviceToken,
  });

  if (
    refreshResponse?.message_type === 'jwt_refreshed' &&
    refreshResponse?.info?.jwt
  ) {
    return refreshResponse.info.jwt;
  }

  throw new Error('JWT refresh response received but no valid JWT token found');
}

/**
 * JWT Bridge: Orchestrate the JWT flow after cookie-based auth.
 * Reads SID2/DT2 cookies, calls refresh_jwt to get a JWT, and
 * stores the JWT in the bridge module for consumers (e.g. Salesforce Live Chat SDK).
 */
export function* executeJwtFlow() {
  try {
    const { sessionId, deviceToken } = loadAuthCookies();
    if (sessionId && deviceToken) {
      const jwt = yield call(refreshJwtToken, sessionId, deviceToken);
      if (jwt) {
        setToken(jwt);
      }
    }
  } catch (error) {
    failRefresh(
      new Error(`[JWT Bridge] JWT flow failed: ${error?.message || error}`),
    );
    captureError(
      new Error(`[JWT Bridge] JWT flow failed: ${error?.message || error}`),
    );
  }
}

export function* signOut(signOutSelectedAction) {
  yield put(SIGN_OUT.start());
  try {
    // Best-effort clear of Salesforce Messaging session before logout.
    // Race against a timeout so a slow/unresponsive Salesforce never blocks logout.
    // Note: SF messaging initialization is gated by the release_chat_salesforce_live_chat_migration feature flag.
    const isSalesforceMessagingInitialized = !!window.embeddedservice_bootstrap;
    if (isSalesforceMessagingInitialized) {
      try {
        yield race({
          clear: call(clearSalesforceMessagingSession, true),
          timeout: call(
            () =>
              new Promise(resolve => {
                setTimeout(resolve, CLEAR_SALESFORCE_SESSION_TIMEOUT_MS);
              }),
          ),
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          '[b2b] Failed to clear Salesforce Messaging session on logout:',
          error,
        );
      }
    }

    const response = yield call(websocketFetch, 'sign_out');
    cookie.remove('SID');
    cookie.remove('SID2');
    cookie.remove('U');
    cookie.remove('R');
    cookie.remove('DISTRICT');
    cookie.remove('employer-admin');
    cookie.remove('SUB_R');
    cancelIdleTimer();
    yield put(SIGN_OUT.success(response, signOutSelectedAction));
  } catch (error) {
    yield put(SIGN_OUT.error(error));
  }
}

export function* authSaga() {
  yield fork(
    takeLatest,
    CONFIGURE_CONNECTION.SUCCEEDED,
    authenticateWithExistingSession,
  );
  yield fork(
    takeLatest,
    [REDIRECT_TO_SIGN_IN, AUTHENTICATE_CONNECTION.ERRORED],
    redirectToSignIn,
  );
  yield fork(takeLatest, SIGN_OUT.BASE, signOut);
  yield fork(takeLatest, SIGN_OUT.SUCCEEDED, redirectToSignIn);

  // JWT Bridge: Automatically run the JWT flow after cookie-based auth succeeds
  yield fork(takeLatest, AUTHENTICATE_CONNECTION.SUCCEEDED, executeJwtFlow);

  // JWT Bridge: Handle on-demand refresh when the JWT expires during a session
  yield fork(takeLatest, JWT_BRIDGE_REFRESH_REQUESTED, executeJwtFlow);
}
